import { ExpandMore } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Collapse } from '@mui/material';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import React, { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import SidebarContext from '../context/SidebarContext';
import { SidebarMenuItemInterface } from '../Interfaces/SideBarInterfaces';
import classes from '../styles/sideBarStyles.module.css';
import SearchBox from '../core-components/theme/components/Searchbox/Searchbox';
import useUserStore from '../store/UserStore';
import { trackEvent } from '../modules/analytics/trackingMiddleware';
import { SIDEBAR_NAV_CLICKED } from '../modules/analytics/events';

interface PropsInterface {
  sideBarMenu?: SidebarMenuItemInterface[];
  refresh?: Function;
  close?: Function;
}

const filterMenu = (menuItems: any[], searchString: string): any[] => {
  const lowerSearch = searchString.toLowerCase();
  return menuItems.reduce((acc, item) => {
    const nameMatches = item.name.toLowerCase().includes(lowerSearch);
    let newItem = { ...item };

    if (item.children) {
      newItem.children = filterMenu(item.children, searchString);
    }

    if (nameMatches || (newItem.children && newItem.children.length > 0)) {
      acc.push(newItem);
    }
    return acc;
  }, [] as any[]);
};

export default function MiniDrawer(props: PropsInterface) {
  const navigate = useNavigate();
  const { open, setOpen } = useContext(SidebarContext);
  const [searchString, setSearchString] = useState<string>('');
  const [menuItemState, setMenuItemState] = useState<{
    [key: string]: boolean;
  }>({});

  const { refresh, close } = props;
  const sideBarMenu = useUserStore().sideBar || [];

  const filteredMenu = useMemo(
    () => filterMenu(sideBarMenu, searchString),
    [sideBarMenu, searchString]
  );

  const handleClick = (name: string) => {
    setMenuItemState(prevState => ({
      ...prevState,
      [name]: !prevState[name],
    }));
    refresh?.();
  };

  const handleOnClose = () => {
    setSearchString('');
    setOpen(false);
  };

  const menuItem = (item: SidebarMenuItemInterface) => {
    if (item.children) {
      return (
        <>
          <ListItem
            key={item.name}
            className={classes.menuItem}
            onClick={() => handleClick(item.name)}
          >
            <ListItemText primary={item.name} className={classes.icon} />
            {menuItemState[item.name] ? (
              <ExpandMore className={classes.icon} />
            ) : (
              <ChevronRightIcon className={classes.icon} />
            )}
          </ListItem>
          <Collapse in={menuItemState[item.name]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{paddingLeft: '15px'}}>
              {item.children.map(child => (
                <React.Fragment key={child.name}>{menuItem(child)}</React.Fragment>
              ))}
            </List>
          </Collapse>
        </>
      );
    } else {
      return (
        <ListItem
          key={item.name}
          className={classes.menuItem}
          onClick={e => {
            e.preventDefault();
            navigate(item.link!);
            trackEvent(SIDEBAR_NAV_CLICKED, { route: item.link!, name: item.name });
            close?.();
            setOpen(false);
            setSearchString('');
            setMenuItemState({});
          }}
        >
          <ListItemText primary={item.name} className={classes.icon} />
        </ListItem>
      );
    }
  };

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={() => handleOnClose()}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.toolbar}>
        <SearchBox
          onChange={e => setSearchString(e.target.value)}
          onClear={() => setSearchString('')}
          borderRadius="40px"
          size="small"
          placeholder="Search ..."
        />
      </div>
      <Divider />
      <List>
        {filteredMenu.map(item => (
          <React.Fragment key={item.name}>{menuItem(item)}</React.Fragment>
        ))}
      </List>
    </Drawer>
  );
}
